import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { rem, size } from 'polished';
import styled, { css } from 'styled-components';
import { isSupported } from 'twilio-video';

import { TOUR_GUIDE_APP_SIDEBAR_CONTAINER, TOUR_GUIDE_SIGN_OUT_BUTTON } from 'constants/tourGuide';
import { breakpoints, misc } from 'helpers/styles/constants';
import { circle, flexCenter } from 'helpers/styles/mixins';
import { logOut } from 'helpers/users';
import { useLayoutData } from 'hooks/useLayoutData';
import routes from 'routes';
import { toggleSidebar } from 'store/layout/actions';
import { useGetCurrentUser } from 'store/users/selectors';
import LogoLink from 'components/AppSidebar/components/LogoLink/LogoLink';
import NavButton from 'components/AppSidebar/components/Nav/NavButton';
import HamburgerMenuButton from 'components/HamburgerMenuButton/HamburgerMenuButton';
import IconArrowThird from 'components/Icons/IconArrowThird';
import IconSignOut from 'components/Icons/Nav/IconSignOut';
import Margin from 'components/Margin/Margin';

import { SessionTimer } from './SessionTimer';

interface WrapperProps {
  sidebarWidth: number;
}

const Wrapper = styled.div<WrapperProps>`
  overflow-x: hidden;
  width: 100%;
  padding-top: ${rem(misc.headerHeightWithMargin)};
  padding-bottom: ${rem(misc.dashboardPaddingXl)};

  @media ${breakpoints.smUp} {
    padding-left: ${({ sidebarWidth }) => rem(sidebarWidth)};
    padding-top: 0;
  }
`;

const Topbar = styled.div`
  position: fixed;
  top: ${isSupported ? '0px' : '80px'};
  left: 0;
  z-index: ${misc.zIndex2};
  width: 100vw;
  background-color: ${({ theme }) => theme.colors.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.catshillWhite};

  @media ${breakpoints.smUp} {
    display: none;
  }
`;

const TopbarContent = styled.div`
  display: flex;
  justify-content: space-between;
  flex-grow: 0;
  flex-shrink: 0;
  height: ${rem(misc.headerHeight)};
  padding: ${rem(14)} ${rem(misc.dashboardPadding)};
  font-size: ${({ theme }) => theme.fontSizes.dbNormal};
  color: ${({ theme }) => theme.colors.cadetBlue};
`;

const Sidebar = styled.div<{ sidebarWidth: number; isCollapsed: boolean; topGap?: number }>`
  position: fixed;
  top: ${({ topGap }) => `${topGap}px` || '0px'};
  left: 0;
  z-index: ${misc.zIndex1};
  flex-grow: 0;
  flex-shrink: 0;
  display: none;
  flex-direction: column;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  width: ${({ sidebarWidth }) => rem(sidebarWidth)};
  height: ${({ topGap }) => (topGap ? `calc(100vh - ${topGap}px)` : '100vh')};
  padding: 0 ${({ isCollapsed }) => rem(isCollapsed ? 10 : 20)};
  font-size: ${({ theme }) => theme.fontSizes.dbNormal};
  background-color: ${({ theme }) => theme.colors.white};
  border-right: 1px solid ${({ theme }) => theme.colors.catshillWhite};
  color: ${({ theme }) => theme.colors.cadetBlue};

  @media ${breakpoints.smUp} {
    display: flex;
  }

  .is-sidebar-freezed & {
    height: auto;
  }
`;

const CollapseIcon = styled(({ isCollapsed, theme, ...rest }) => <IconArrowThird {...rest} />)`
  ${size(rem(9), rem(6))};
  display: block;
  stroke: ${({ theme }) => theme.colors.bermudaGray};
  stroke-width: 2;
  transform: rotateZ(${({ isCollapsed }) => (isCollapsed ? '0' : '180deg')});
  transition: stroke 0.3s ease;
`;

const ToggleButton = styled.button<{ sidebarWidth: number; topGap?: number }>`
  z-index: ${misc.zIndex2};
  position: fixed;
  top: ${({ topGap }) => (topGap ? `calc(40px + ${topGap}px)` : '40px')};
  left: ${({ sidebarWidth }) => rem(sidebarWidth)};
  display: none;
  ${circle('24px')};
  margin: 0;
  padding: 0;
  outline: none;
  border: 1px solid ${({ theme }) => theme.colors.catshillWhite};
  background-color: ${({ theme }) => theme.colors.white};
  transform: translateX(-50%);

  @media ${breakpoints.smUp} {
    ${flexCenter()};
  }

  &:hover {
    ${CollapseIcon} {
      stroke: ${({ theme }) => theme.colors.main};
    }
  }
`;

const Nav = styled.div<{ isActive: boolean; topGap: number }>`
  position: fixed;
  top: ${({ topGap }) => rem(misc.headerHeight + topGap)};
  left: 100vw;
  overflow-y: auto;
  width: ${rem(300)};
  padding: ${rem(20)} ${rem(10)} 0 ${rem(10)};
  max-width: 100%;
  max-height: calc(100vh - ${rem(misc.headerHeight)});
  box-shadow: -1px 6px 6px 1px rgba(0, 0, 0, 0.1);
  background-color: ${({ theme }) => theme.colors.white};
  transition: transform 0.2s ease;
  -webkit-overflow-scrolling: touch;

  ${({ isActive }) =>
    isActive &&
    css`
      transform: translateX(-100%);
    `};
`;

const SignOutWrapper = styled.div<{ isCollapsed: boolean }>`
  display: flex;
  margin-top: ${rem(20)};

  ${({ isCollapsed }) => isCollapsed && 'justify-content: center'};
`;

const SessionTimerWrapper = styled.div`
  position: absolute;
  top: ${rem(10)};
  right: ${rem(10)};
`;

const TARGET_ELEMENTS_CHECK = ['path', 'svg', 'SPAN', 'BUTTON'];

interface Props {
  children: React.ReactNode | React.ReactNode[];
  sidebar?: React.ReactNode;
  canBeToggled?: boolean;
}

const Content = ({ children, sidebar, canBeToggled }: Props) => {
  const dispatch = useDispatch();
  const [isNavActive, setIsNavActive] = useState(false);
  const navRef = useRef() as React.MutableRefObject<HTMLDivElement>;
  const hasSidebar = Boolean(sidebar);
  const { sidebarWidth, isSidebarCollapsed } = useLayoutData(canBeToggled);
  const currentUser = useGetCurrentUser();

  const toggleNav = () => setIsNavActive(!isNavActive);

  const logOutUser = useCallback(() => logOut(routes.root.path()), []);
  const handleToggleSidebar = useCallback(() => dispatch(toggleSidebar()), [dispatch]);

  useEffect(() => {
    const handleClosenav = (event: MouseEvent) => {
      const target = event.target as Element;

      if (target.id === 'additionalAdviserPopupButton') return;

      if (!navRef.current.contains(target)) {
        // exclude closing in hamburger icon click.
        if (target.nodeName === 'path') return;

        if (isNavActive) setIsNavActive(false);
      }

      if (navRef.current.contains(target) && TARGET_ELEMENTS_CHECK.includes(target.nodeName)) {
        if (isNavActive) setIsNavActive(false);
      }
    };

    if (isNavActive) document.addEventListener('click', handleClosenav, true);

    return () => document.removeEventListener('click', handleClosenav, true);
  }, [isNavActive]);

  const warningBannerHeight = isSupported ? 0 : 80;
  return (
    <>
      <Topbar>
        <TopbarContent>
          <LogoLink />

          {hasSidebar && (
            <>
              <HamburgerMenuButton onClick={toggleNav} />
              <Nav ref={navRef} isActive={isNavActive} topGap={warningBannerHeight}>
                <>
                  {currentUser && (
                    <>
                      <Margin left={10} bottom={10}>
                        <NavButton
                          icon={<IconSignOut />}
                          text={<FormattedMessage id="shared.client_nav.signOut" />}
                          onClick={logOutUser}
                        />
                      </Margin>

                      <SessionTimerWrapper>
                        <SessionTimer />
                      </SessionTimerWrapper>
                    </>
                  )}
                  {sidebar}
                </>
              </Nav>
            </>
          )}
        </TopbarContent>
      </Topbar>

      <Sidebar
        data-test={TOUR_GUIDE_APP_SIDEBAR_CONTAINER}
        id={TOUR_GUIDE_APP_SIDEBAR_CONTAINER}
        sidebarWidth={sidebarWidth}
        isCollapsed={isSidebarCollapsed}
        topGap={warningBannerHeight}
      >
        {currentUser && (
          <>
            <SignOutWrapper isCollapsed={isSidebarCollapsed}>
              <NavButton
                id={TOUR_GUIDE_SIGN_OUT_BUTTON}
                icon={<IconSignOut />}
                text={<FormattedMessage id="shared.client_nav.signOut" />}
                onClick={logOutUser}
              />
            </SignOutWrapper>

            {!isSidebarCollapsed && (
              <SessionTimerWrapper>
                <SessionTimer />
              </SessionTimerWrapper>
            )}
          </>
        )}

        <Margin top={20} bottom={20}>
          <LogoLink />
        </Margin>

        {sidebar}
      </Sidebar>

      {canBeToggled && (
        <ToggleButton
          onClick={handleToggleSidebar}
          sidebarWidth={sidebarWidth}
          topGap={warningBannerHeight}
        >
          <CollapseIcon isCollapsed={isSidebarCollapsed} />
        </ToggleButton>
      )}

      <Wrapper sidebarWidth={sidebarWidth}>{children}</Wrapper>
    </>
  );
};

export default Content;
